import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';

import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { CancelButton } from '@src/custom';

import {
    Toolbar,
    TextInput,
    useSaveContext,
    useRedirect,
    useResourceContext,
    useRecordContext,
    useNotify,
} from 'react-admin';

const EditWithNewVersionToolbar = (props) => {
    const [open, setOpen] = React.useState(false);
    const { save } = useSaveContext();
    const { handleSubmit, getValues } = useFormContext();
    const redirect = useRedirect();
    const resource = useResourceContext();
    const record = useRecordContext();
    const notify = useNotify();

    const handleClick = handleSubmit(() => {
        setOpen(true);
    });

    const handleCancel = useCallback(() => {
        setOpen(false);
    }, [setOpen]);

    const handleSave = useCallback(async (e) => {
        e.preventDefault();

        try {
            const formData = getValues();
            // In React Admin 5, we can use the onSuccess option to explicitly redirect
            // after the save operation is completed successfully.
            await save(formData, {
                onSuccess(data) {
                    setOpen(false);
                    // Redirect to the edit page with the (potentially updated) record ID
                    const recordId = data?.id || record?.id;
                    if (recordId) {
                        notify('Element Updated', { type: 'success' });
                        redirect('edit', resource, recordId, data);
                    }
                },
                onError(error) {
                    console.error('Error saving form:', error);
                    notify(error.message || 'Error saving form', { type: 'error' });
                    setOpen(false);
                },
            });
        } catch (error) {
            console.error('Error in handleSave:', error);
            setOpen(false);
        }
    }, [save, getValues, redirect, resource, record, notify]);

    return (
        <Toolbar {...props}>
            <Button
                variant="contained"
                color="primary"
                onClick={handleClick}
            >
                Save Changes
            </Button>
            <Dialog
                fullWidth
                open={open}
                onClose={handleCancel}
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center">
                        <Box flexGrow={1}>Version Notes</Box>
                        <Box>
                            <IconButton onClick={handleCancel}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <TextInput
                        fullWidth
                        multiline
                        label="Version Description (optional)"
                        source="version_description"
                        resource={props.resource}
                    />
                    <Box display="flex" mb={2} mt={2}>
                        <Box mr="1rem">
                            <Button
                                autoFocus
                                variant="contained"
                                color="primary"
                                onClick={handleSave}
                            >
                                Save Changes
                            </Button>
                        </Box>
                        <Box>
                            <CancelButton title="Cancel Changes" onClick={handleCancel} />
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
        </Toolbar>
    );
};

EditWithNewVersionToolbar.propTypes = {
    resource: PropTypes.string,
};

export default EditWithNewVersionToolbar;
